
import React from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';

// Domain validation regex pattern
const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

const formSchema = z.object({
  domain: z.string()
    .min(4, "Domain must be at least 4 characters")
    .max(253, "Domain is too long")
    .regex(domainRegex, "Please enter a valid domain (e.g., example.com)")
});

type InstallationFormValues = z.infer<typeof formSchema>;

interface AddInstallationDialogProps {
  subscriptionId: string;
}

const AddInstallationDialog = ({ subscriptionId }: AddInstallationDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<InstallationFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      domain: "",
    },
  });

  const onSubmit = async (data: InstallationFormValues) => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const installationKey = crypto.randomUUID();
      const { error } = await supabase
        .from('website_installations')
        .insert({
          domain: data.domain.toLowerCase().trim(), // Normalize domain
          user_id: user.id,
          subscription_id: subscriptionId,
          installation_key: installationKey,
          status: 'active',
        });

      if (error) {
        if (error.code === '23505') { // Unique violation error code
          toast({
            title: "Domain already exists",
            description: "This domain already has an installation. Please use a different domain.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      toast({
        title: "Installation added",
        description: "Your new website installation has been created.",
      });

      queryClient.invalidateQueries({
        queryKey: ['installations', user.id],
      });

      setOpen(false);
      form.reset();
    } catch (error) {
      console.error('Error adding installation:', error);
      toast({
        title: "Error",
        description: "Failed to add installation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Add Installation
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Installation</DialogTitle>
          <DialogDescription>
            Add a new website domain to install the accessibility widget.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website Domain</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="example.com" 
                      {...field} 
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isSubmitting}
              >
                Add Installation
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInstallationDialog;
